import { useQuery } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import type { FindViewConfigurationPage, FindViewConfigurationPageVariables } from 'types/graphql';
import type { ViewConfigPayload, ViewConfigRow } from './viewConfig.store';

/**
 * Every saved view of one grid page, read through `viewConfigurationPage` (P7-R35).
 *
 *   open ─▶ page 1 (first 100, id asc) ─▶ hasNextPage? ─▶ fetchMore(after: endCursor) ─▶ … ─▶ rows
 *                                                   └─ no ─▶ rows (complete)
 *
 * Each request is bounded; the walk is not. The menu and "Save changes" need the whole set, so the
 * list is `undefined` until the last page has arrived and is never handed out truncated.
 */
export const VIEW_CONFIGURATION_PAGE_SIZE = 100;

export const FIND_VIEW_CONFIGURATION_PAGE = gql`
  query FindViewConfigurationPage($page: String!, $input: StandardPageInput) {
    viewConfigurationPage(page: $page, input: $input) {
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        node {
          id
          name
          config
        }
      }
    }
  }
`;

/** Page 1's variables. The create refetch uses the same ones, so it lands on the cache entry this hook reads. */
export const viewConfigurationPageVariables = (page: string): FindViewConfigurationPageVariables => ({
  page,
  input: { first: VIEW_CONFIGURATION_PAGE_SIZE, sortItems: [{ field: 'id', sort: 'asc' }] },
});

/** A saved config is always an object (`dataGridStateToConfig`); the JSON scalar is narrowed, not cast (FU-VC-T). */
const isViewConfigPayload = (value: unknown): value is ViewConfigPayload =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

type PageEdges = FindViewConfigurationPage['viewConfigurationPage']['edges'];

const toRows = (edges: PageEdges): ViewConfigRow[] =>
  (edges ?? []).flatMap((edge) =>
    edge
      ? [
          {
            id: edge.node.id,
            name: edge.node.name,
            config: isViewConfigPayload(edge.node.config) ? edge.node.config : {},
          },
        ]
      : []
  );

export function useViewConfigurationList(page: string): ViewConfigRow[] | undefined {
  const variables = useMemo(() => viewConfigurationPageVariables(page), [page]);
  const { data, fetchMore } = useQuery<FindViewConfigurationPage, FindViewConfigurationPageVariables>(
    FIND_VIEW_CONFIGURATION_PAGE,
    { fetchPolicy: 'cache-first', variables }
  );
  const connection = data?.viewConfigurationPage;
  const nextCursor = connection?.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;

  useEffect(() => {
    if (!nextCursor) return;
    void fetchMore({
      variables: { input: { ...variables.input, after: nextCursor } },
      updateQuery: (previous, { fetchMoreResult }) => ({
        viewConfigurationPage: {
          ...fetchMoreResult.viewConfigurationPage,
          edges: [
            ...(previous.viewConfigurationPage.edges ?? []),
            ...(fetchMoreResult.viewConfigurationPage.edges ?? []),
          ],
        },
      }),
    });
  }, [nextCursor, fetchMore, variables]);

  return useMemo(
    () => (connection && !connection.pageInfo?.hasNextPage ? toRows(connection.edges) : undefined),
    [connection]
  );
}
